import format from 'date-fns/format';
import cookiePolicyContent from '@/content/policies/cookiePolicy';
import { getCookie, setCookie } from '@/lib/cookie-helpers';
import bam from '@/lib/bam';
import useCurrentUser from '@/hooks/useCurrentUser/useCurrentUser';
import { computed, reactive, ref } from '@nuxtjs/composition-api';

let initialized;

const dependencyReadiness = reactive({
  contentful: true, // Static content always ready
  userHook: false,
  ip: false,
});

const dependencies = reactive({
  companyId: '',
  userId: '',
  ip: '',
});

// Initialize policy from static content
const policy = reactive<{
  header: string;
  body: string;
  version: string;
  updatedDate: string;
  formattedUpdatedDate: string;
  bannerCopy: string;
  agreeCta: string;
}>({
  header: cookiePolicyContent.header,
  body: cookiePolicyContent.body,
  version: cookiePolicyContent.updatedDate
    ? format(new Date(cookiePolicyContent.updatedDate), 'MMddyyyyHHmm')
    : '',
  updatedDate: cookiePolicyContent.updatedDate,
  formattedUpdatedDate: cookiePolicyContent.updatedDate
    ? format(new Date(cookiePolicyContent.updatedDate), 'MMMM dd, yyyy')
    : '',
  bannerCopy: cookiePolicyContent.bannerCopy,
  agreeCta: cookiePolicyContent.agreeCta || 'Acknowledge',
});

const cookieStr = computed(() => `cookie-policy-v-${policy.version}`);

const hasAcknowledged = ref(false);

const acknowledge = () => {
  hasAcknowledged.value = true;
  setCookie(cookieStr.value, 'true', {
    // Cookie expires in 1 year
    expires: 365,
  });
};

const isReady = computed(() => {
  return Object.values(dependencyReadiness).every((v) => !!v);
});

const trackCookieEvent = (eventName: string, additionalContext = {}) => {
  bam.track(eventName, {
    ...additionalContext,
    lastUpdated: policy.updatedDate,
    userId: dependencies.userId || '',
    companyId: dependencies.companyId || '',
    ip: dependencies.ip,
  });
};

const setupHook = async () => {
  // Fetches the IP address of the current device
  fetch('https://api.ipify.org?format=json')
    .then((response) => response.json())
    .then((data) => {
      dependencies.ip = data.ip;
    })
    .finally(() => {
      dependencyReadiness.ip = true;
    });

  // Check if user has already acknowledged this version
  const cookie = getCookie(cookieStr.value);
  hasAcknowledged.value = cookie === 'true';

  const { currentUserId, companyId } = useCurrentUser();
  // @ts-ignore
  dependencies.companyId = companyId;
  // @ts-ignore
  dependencies.userId = currentUserId;
  dependencyReadiness.userHook = true;
};

const useCookiePolicy = (forceTest = false) => {
  if (!initialized || forceTest) {
    initialized = true;
    setupHook();
  }

  return {
    isReady,
    hasAcknowledged,
    acknowledge,
    policy,
    trackCookieEvent,
  };
};

export default useCookiePolicy;
